import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, Download, ScanLine, ArrowLeft, FileText, AlertTriangle } from "lucide-react";
import api, { API } from "@/lib/apiClient";
import SEO from "@/components/SEO";

const STATUS_CLS = {
  ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  SUPERSEDED: "bg-amber-50 text-amber-700 border-amber-200",
  WITHDRAWN: "bg-red-50 text-red-600 border-red-200",
  ARCHIVED: "bg-slate-100 text-slate-500 border-slate-200",
};
const fmtDate = (iso) => {
  const d = new Date((iso || "") + "T00:00:00");
  return isNaN(d) ? (iso || "—") : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();
};

export default function OfficialDocumentDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(undefined);
  const [showPdf, setShowPdf] = useState(false);

  useEffect(() => { api.get(`/official-documents/detail/${slug}`).then(({ data }) => setD(data)).catch(() => setD(null)); }, [slug]);

  if (d === undefined) return <div className="py-24 text-center text-npst-muted animate-pulse">Loading document…</div>;
  if (d === null) return (
    <div className="py-24 text-center">
      <p className="font-serif text-3xl text-npst-ink">Document not found</p>
      <Link to="/official-documents" className="inline-block mt-4 text-npst-navy link-underline">Back to Official Documents</Link>
    </div>
  );

  const pdfUrl = `${API}/official-documents/download/${d.doc_id}`;
  return (
    <div className="fade-in">
      <SEO title={`${d.title} — NPST Official Document`} description={d.description || `${d.category} · ${d.reference_no} · Issued by ${d.authority}`} canonical={`${window.location.origin}/official-documents/${d.slug}`} />
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/official-documents" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-npst-muted hover:text-npst-navy mb-6" data-testid="back-to-docs"><ArrowLeft size={13} /> Official Documents</Link>

        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-npst-gold">{d.category}</span>
          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_CLS[d.status] || STATUS_CLS.ARCHIVED}`} data-testid="detail-status">{d.status}</span>
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-npst-ink tracking-tight leading-tight" data-testid="detail-title">{d.title}</h1>

        {d.status === "SUPERSEDED" && d.superseded && (
          <div className="mt-5 bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3" data-testid="detail-superseded">
            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <span className="font-semibold">This document has been superseded.</span> {d.superseded.note}
              {d.superseded.slug && <Link to={`/official-documents/${d.superseded.slug}`} className="block mt-1 font-semibold link-underline" data-testid="view-latest-doc">View Latest Document →</Link>}
            </div>
          </div>
        )}

        <div className="mt-6 bg-white border border-npst-line rounded-md divide-y divide-npst-line">
          {[["Document ID", <span className="font-mono">{d.doc_id}</span>], ["Category", d.category],
            ["Reference Number", d.reference_no || "—"], ["Document Date", fmtDate(d.doc_date)],
            ["Issuing Authority", d.authority || "—"], ["Number of Pages", d.pages],
            ["Published / Uploaded", fmtDate((d.published_at || "").slice(0, 10))], ["Current Status", d.status]].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 px-5 py-3 text-sm">
              <span className="text-npst-muted uppercase tracking-wider text-xs font-semibold shrink-0">{k}</span>
              <span className="text-npst-ink font-medium text-right break-all">{v}</span>
            </div>
          ))}
          {d.sha256 && (
            <div className="px-5 py-3">
              <div className="text-npst-muted uppercase tracking-wider text-xs font-semibold mb-1">SHA-256 Fingerprint</div>
              <div className="font-mono text-[11px] text-npst-inkSoft break-all" data-testid="detail-sha">{d.sha256}</div>
            </div>
          )}
        </div>

        {d.description && <p className="mt-6 text-npst-inkSoft leading-relaxed">{d.description}</p>}
        {d.keywords?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {d.keywords.map((k) => <span key={k} className="text-xs bg-npst-bgAlt border border-npst-line rounded-full px-3 py-1 text-npst-muted">{k}</span>)}
          </div>
        )}

        <div className="flex flex-wrap gap-3 mt-8">
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-npst-navy text-white text-sm font-semibold uppercase tracking-[0.1em] rounded-sm hover:bg-npst-navyDeep transition-colors" data-testid="detail-download"><Download size={15} /> Download Verified PDF</a>
          <Link to={`/verify/${d.doc_id}`} className="inline-flex items-center gap-2 px-6 py-3 border border-npst-navy text-npst-navy text-sm font-semibold uppercase tracking-[0.1em] rounded-sm hover:bg-npst-navy hover:text-white transition-colors" data-testid="detail-verify"><ShieldCheck size={15} /> Verify Authenticity</Link>
          {!showPdf && <button onClick={() => setShowPdf(true)} className="inline-flex items-center gap-2 px-6 py-3 border border-npst-line text-npst-navy text-sm font-semibold uppercase tracking-[0.1em] rounded-sm hover:border-npst-gold transition-colors" data-testid="detail-preview-btn"><FileText size={15} /> Preview Document</button>}
        </div>

        {showPdf && (
          <div className="mt-8 border border-npst-line rounded-md overflow-hidden bg-slate-50" data-testid="detail-pdf-viewer">
            <iframe src={pdfUrl} title={d.title} className="w-full" style={{ height: "80vh" }} />
          </div>
        )}
      </section>
    </div>
  );
}
